import { useMemo, useState } from "react";
import { Box, Typography, Tabs, Tab, useTheme, alpha } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { useResponsive } from "../../../core/hooks/useResponsive";

function formatInr(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n));
}

const ChartCard = ({ title, subtitle, children, minHeight = 280 }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        p: 1.75,
        height: "100%",
        minHeight,
        border: `1px solid ${alpha(primary, 0.14)}`,
        background:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.85)
            : `linear-gradient(180deg, #fff 0%, ${alpha(primary, 0.03)} 100%)`,
        boxShadow: `0 8px 24px ${alpha("#000", 0.05)}`,
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} letterSpacing="-0.01em">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
};

export function DashboardCharts({ cashFlow, holdings = [], tradePerformance, singleColumn = false, compact = false, variant = "full" }) {
  const theme = useTheme();
  const { isPhone, isCompact } = useResponsive();
  const [activeTab, setActiveTab] = useState(0);
  const primary = theme.palette.primary.main;
  const profit = theme.palette.success?.main || "#2e7d32";
  const loss = theme.palette.error?.main || "#c62828";
  const neutral = theme.palette.text.secondary;

  const budgetData = useMemo(() => {
    const invested = Math.max(0, Number(cashFlow?.invested) || 0);
    const balance = Math.max(0, Number(cashFlow?.balance) || 0);
    const budget = Number(cashFlow?.tradeBudget) || invested + balance;
    const other = Math.max(0, budget - invested - balance);
    const rows = [
      { name: "Invested", value: invested, color: primary },
      { name: "Available", value: balance, color: alpha(primary, 0.45) },
    ];
    if (other > 0.01) rows.push({ name: "Unallocated", value: other, color: alpha(neutral, 0.35) });
    return rows.filter((d) => d.value > 0);
  }, [cashFlow, primary, neutral]);

  const outcomeData = useMemo(() => {
    const p = Number(tradePerformance?.profitTrades) || 0;
    const l = Number(tradePerformance?.lossTrades) || 0;
    const t = Number(tradePerformance?.totalTrades) || 0;
    const neutralCount = Math.max(0, t - p - l);
    const rows = [
      { name: "Winning closes", value: p, color: profit },
      { name: "Losing closes", value: l, color: loss },
    ];
    if (neutralCount > 0) rows.push({ name: "Other", value: neutralCount, color: alpha(neutral, 0.5) });
    return rows.filter((d) => d.value > 0);
  }, [tradePerformance, profit, loss, neutral]);

  const holdingsSummary = useMemo(() => {
    const palette = [primary, alpha(primary, 0.75), alpha(primary, 0.6), alpha(primary, 0.45), alpha(primary, 0.3), alpha(primary, 0.18)];
    const topRows = (holdings || [])
      .map((h) => ({
        name: h.symbol || String(h.token),
        value: Math.max(0, Number(h.invested) || 0),
      }))
      .filter((h) => h.value > 0)
      .sort((a, b) => b.value - a.value);
    const topFive = topRows.slice(0, 5);
    const others = topRows.slice(5).reduce((sum, row) => sum + row.value, 0);
    const rows = topFive.map((row, i) => ({ ...row, color: palette[i % palette.length] }));
    if (others > 0) rows.push({ name: "Others", value: others, color: alpha(neutral, 0.45) });
    return rows;
  }, [holdings, primary, neutral]);

  // Phones get a slightly smaller pie so the legend never collides with the arc.
  const chartHeight = isPhone ? 200 : compact ? 205 : 250;
  const chartCardMinHeight = isPhone ? 250 : compact ? 255 : 295;
  const outerRadius = isPhone ? 72 : compact ? 82 : 88;
  const legendFontSize = isPhone ? 11 : 12;
  const isHoldingsSideVariant = variant === "holdings-side";

  const pieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${alpha(primary, 0.2)}`,
          borderRadius: 1,
          px: 1,
          py: 0.75,
          boxShadow: 2,
        }}
      >
        <Typography variant="caption" fontWeight={700}>
          {p.name}
        </Typography>
        <Typography variant="body2">{formatInr(p.value)}</Typography>
      </Box>
    );
  };

  const renderPie = (rows, innerRadius, emptyLabel, cy = "48%") => {
    if (!rows.length) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: isPhone ? 180 : 220 }}>
          <Typography color="text.secondary">{emptyLabel}</Typography>
        </Box>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <PieChart>
          <Pie data={rows} cx="50%" cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} paddingAngle={2} dataKey="value" nameKey="name">
            {rows.map((entry, i) => (
              <Cell key={i} fill={entry.color} stroke={alpha("#fff", 0.06)} />
            ))}
          </Pie>
          <Tooltip content={pieTooltip} />
          <Legend
            verticalAlign="bottom"
            height={isPhone ? 36 : 28}
            formatter={(v) => <span style={{ fontSize: legendFontSize, fontWeight: 600 }}>{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // One descriptor per chart, so the tabbed and gridded layouts stay in sync.
  const charts = useMemo(() => {
    const list = [
      {
        key: "capital",
        tab: "Capital",
        title: "Capital allocation",
        subtitle: "Invested vs available from trade budget",
        rows: budgetData,
        innerRadius: isPhone ? 44 : 52,
        cy: "48%",
        empty: "No budget data",
      },
    ];
    if (isHoldingsSideVariant) {
      list.push({
        key: "holdingPerformance",
        tab: "Holdings P/L",
        title: "Holding performance",
        subtitle: "Total profit vs total loss (open holdings)",
        // rows come from holdingPerformanceData via rowsFor()
        innerRadius: isPhone ? 40 : 48,
        cy: "48%",
        empty: "No holding P/L data",
      });
    } else {
      list.push(
        {
          key: "outcome",
          tab: "Closed mix",
          title: "Closed trades mix",
          subtitle: "Historical win / loss counts (paper or live)",
          rows: outcomeData,
          innerRadius: isPhone ? 44 : 52,
          cy: "50%",
          empty: "No closed trade stats yet",
        },
        {
          key: "holdingsSummary",
          tab: "Top holdings",
          title: "Holding summary",
          subtitle: "Top holdings by invested value",
          rows: holdingsSummary,
          innerRadius: isPhone ? 42 : 50,
          cy: "50%",
          empty: "No open positions",
        }
      );
    }
    return list;
  }, [budgetData, outcomeData, holdingsSummary, isHoldingsSideVariant, isPhone]);

  const holdingPerformanceData = useMemo(() => {
    const totalProfit = (holdings || []).reduce((sum, row) => {
      const value = Number(row?.profitLoss) || 0;
      return value > 0 ? sum + value : sum;
    }, 0);
    const totalLoss = (holdings || []).reduce((sum, row) => {
      const value = Number(row?.profitLoss) || 0;
      return value < 0 ? sum + Math.abs(value) : sum;
    }, 0);
    const rows = [];
    if (totalProfit > 0) rows.push({ name: "Total profit", value: totalProfit, color: profit });
    if (totalLoss > 0) rows.push({ name: "Total loss", value: totalLoss, color: loss });
    return rows;
  }, [holdings, profit, loss]);

  const rowsFor = (chart) => (chart.key === "holdingPerformance" ? holdingPerformanceData : chart.rows || []);

  // Below 768px, stacking every pie costs ~255px of scroll each for charts that are
  // glanced at, not studied. A tab strip keeps all of them one tap away instead.
  if (isCompact) {
    const safeTab = Math.min(activeTab, charts.length - 1);
    const active = charts[safeTab];
    return (
      <Box
        sx={{
          borderRadius: 2.5,
          border: `1px solid ${alpha(primary, 0.14)}`,
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.85)
              : `linear-gradient(180deg, #fff 0%, ${alpha(primary, 0.03)} 100%)`,
          boxShadow: `0 8px 24px ${alpha("#000", 0.05)}`,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={safeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            borderBottom: `1px solid ${alpha(primary, 0.12)}`,
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.78rem",
              px: 1,
            },
          }}
        >
          {charts.map((c) => (
            <Tab key={c.key} label={c.tab} />
          ))}
        </Tabs>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={800} letterSpacing="-0.01em">
            {active.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {active.subtitle}
          </Typography>
          {renderPie(rowsFor(active), active.innerRadius, active.empty, active.cy)}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: singleColumn
            ? "1fr"
            : { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(auto-fit, minmax(300px, 1fr))" },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        {charts.map((chart) => (
          <ChartCard key={chart.key} title={chart.title} subtitle={chart.subtitle} minHeight={chartCardMinHeight}>
            {renderPie(rowsFor(chart), chart.innerRadius, chart.empty, chart.cy)}
          </ChartCard>
        ))}
      </Box>
    </Box>
  );
}
